
import {
  billingChangeRequestMapState,
  billingChangeRequestMapActions,
  billingChangeRequestMapMutations,
  billingChangeRequestMapGetters
} from "@/store/modules/billingchangerequest";
import { companiesMapGetters } from "@/store/modules/companies";
import { omit, get } from "lodash";
import { StorageFormStatus } from "@/helpers/storageFormBaseInterface";
import { authMapGetters } from "@/store/modules/auth";
import { printSingleChangeRequest } from "@/helpers/downloadLossForm";
import { getPrintMethod } from "@/helpers/printMethodHelper";
import PrintMethodModal from "@/components/Dialog/PrintMethodModal.vue";
import ActivitiesMixin from "@/components/ActivitiesView/ActivitiesMixin.vue";

import * as types from "@/store/mutation-types";
import { objectDifference } from "@/helpers";
import { object } from "dot-object";
import ChangeRequestMixin from "../../ChangeRequests/ChangeRequestMixin";
import VueWithMixins from "@/helpers/mixins";
interface IBilling {
  errorMessage: string;
  successMessage: string;
  resourceNotFound: boolean;
  didInit: boolean;
  billChangeRequestData: any;
  pageDataInitialized: boolean;
}

export default VueWithMixins(ChangeRequestMixin).extend({
  name: "BillingChangeRequestDetailsView",
  mixins: [ActivitiesMixin],
  data(): IBilling {
    return {
      errorMessage: "",
      successMessage: "",
      resourceNotFound: false,
      didInit: false,
      billChangeRequestData: {},
      pageDataInitialized: false
    };
  },
  components: {
    PrintMethodModal,
    // DataView
    ViewChangeRequestInsurerDetails: () =>
      import("@/components/ChangeRequests/ViewChangeRequestInsurerDetails.vue"),
    ViewChangeRequestPolicyDetails: () =>
      import("@/components/ChangeRequests/ViewChangeRequestPolicyDetails.vue"),
    ChangeRequestStatus: () =>
      import("@/components/ChangeRequests/ChangeRequestStatus.vue"),
    DisplayAttachment: () =>
      import("@/components/DataView/DisplayAttachment.vue")
  },
  computed: {
    ...authMapGetters(["getCurrentUser"]),
    billingChangeRequestId(): string {
      return this.$route.params.billingchangerequest_id;
    },
    ...billingChangeRequestMapState(["makingApiRequest", "editing"]),
    ...billingChangeRequestMapGetters(["getBillingChangeRequestById"]),
    original(): any {
      return this.getBillingChangeRequestById(this.billingChangeRequestId);
    },
    showApprovedButton(): boolean {
      return !!(
        this.editing &&
        (this.editing as any).status !== "approved" &&
        (this.editing as any).status !== "rejected"
      );
    },
    showDeclineButton(): boolean {
      return !!(
        this.editing &&
        (this.editing as any).status !== "rejected" &&
        (this.editing as any).status !== "approved"
      );
    },
    ...companiesMapGetters(["companies"]),
    agentApplicationID(): any {
      return this.$route.params.agentApplicationID;
    },
    updatedFields(): any {
      const toIgnore = ["data.agentInfo"];
      return omit(
        this.editing && this.original
          ? objectDifference(this.editing, this.original)
          : {},
        toIgnore
      );
    },
    primaryToolbar(): any {
      if (this.editing) {
        return {
          text: "Save",
          key: "save",
          loading: this.makingApiRequest,
          disabled: Object.keys(this.updatedFields).length === 0
        };
      }
      return undefined;
    },
    topButtonSecondaryItems(): {
      text: string;
      key: string;
    } | null {
      if (get(this.editing, "deleted") || get(this.editing, "isLegacy")) {
        return null;
      }
      let options = [
        {
          title: "Print",
          command: "print"
        }
      ];
      let secondaryMenu = {
        text: "Actions",
        key: "actions",
        subItems: options
      };
      if (
        get(this.original, "status") === "Not Submitted" ||
        (get(this.getCurrentUser, "role") === "admin" &&
          get(this.original, "status") !== "Received" &&
          get(this.original, "status") !== "Submitted" &&
          get(this.original, "status") !== "Issued" &&
          !get(this.original, "printed"))
      ) {
        options.push({
          title: "Edit",
          command: "edit"
        });
      }
      return secondaryMenu;
    },
    queryOverride(): any {
      return {
        "data.cancellationChangeRequestId": this.billingChangeRequestId,
        activityType: "CancellationChangeRequestActivity"
      };
    }
  },
  created() {
    this.initializePageData();
  },
  destroyed() {},
  methods: {
    ...billingChangeRequestMapActions([
      "getBillingChangeRequest",
      "updateBillingChangeRequest"
    ]),
    ...billingChangeRequestMapMutations({
      editField: types.SET_EDIT_FIELD
    }),
    getValue(data: object, property: string): string {
      return data ? `${data}.${property}` : "";
    },
    // function to get the property of the object
    getProperty(data: object, property: keyof typeof object): string {
      return data ? data[property] : "";
    },
    onToolbarItemSelected(event: string): void {
      switch (event) {
        case "save": {
          try {
            this.Submit();
          } catch (error) {
            this.$appNotifyError(error.message);
            this.$bugSnagClient.notify(error);
          }
          break;
        }
        case "edit":
          this.$router
            .push(`/billingchangerequest/edit/${this.editing?._id}`)
            .catch(() => {});
          break;
        case "print":
          this.printCallback();
          break;
        default:
          break;
      }
    },
    async Submit() {
      if (this.editing) {
        const { status } = this.editing;
        await this.updateBillingChangeRequest({
          id: this.billingChangeRequestId,
          update: {
            status
          }
        });

        this.$appNotifySuccess("Status changed");
      }
    },
    async initializePageData() {
      try {
        await this.getBillingChangeRequest(this.billingChangeRequestId);
        const { data, ...rootFields } = this.editing as any;
        this.billChangeRequestData = {
          ...rootFields,
          ...data
        };
        this.pageDataInitialized = true;
      } catch (error) {
        this.resourceNotFound = true;
        this.$bugSnagClient.notify(error);
      }
    },
    changeStatus(action: StorageFormStatus): void {
      this.editField({
        key: "status",
        value: action
      });
    },
    convertBooleanToText(value: boolean): string {
      return value ? "Yes" : "No";
    },
    toTitleCase(str: string): string {
      return str.replace(/([A-Z])/g, " $1").replace(/^./, function(str) {
        return str.toUpperCase();
      });
    },
    async printCallback() {
      const printMethod = await getPrintMethod(this);
      const policyPrefix =
        this.editing &&
        this.editing.data &&
        this.editing.data.policyInfo &&
        this.editing.data.policyInfo.policyPrefix
          ? this.editing.data.policyInfo.policyPrefix
          : undefined;
      await printSingleChangeRequest.bind(this)({
        changeRequestIds: this.editing?._id,
        policyPrefix,
        printMethod,
        printAction: "billingChangeRequest/printBillingChangeRequest"
      });
    }
  }
});
